import { Injectable } from '@angular/core';

export interface DeviceInfo {
  ip: string;
  userAgent: string;
}

@Injectable({
  providedIn: 'root'
})
export class DeviceInfoService {
  private readonly unavailableValue = 'No disponible';
  private readonly sessionStorageKey = 'device-info-public-ip';
  private cachedPublicIP: string | null = null;

  private readonly publicIPProviders = [
    { url: 'https://api.ipify.org?format=json', key: 'ip' },
    { url: 'https://api64.ipify.org?format=json', key: 'ip' },
    { url: 'https://ipapi.co/json/', key: 'ip' },
    { url: 'https://ipinfo.io/json', key: 'ip' }
  ];

  async getCompleteDeviceInfo(): Promise<DeviceInfo> {
    return {
      ip: await this.resolvePublicIP(),
      userAgent: this.buildParsedUserAgent()
    };
  }

  private buildParsedUserAgent(): string {
    const rawUserAgent = navigator.userAgent || '';
    const browser = this.detectBrowser(rawUserAgent);
    const browserVersion = this.detectBrowserVersion(rawUserAgent);
    const os = this.detectOS(rawUserAgent);
    const deviceType = this.detectDeviceType(rawUserAgent);

    return `${browser} ${browserVersion} | ${os} | ${deviceType}`;
  }

  private async resolvePublicIP(): Promise<string> {
    const cachedIP = this.getCachedPublicIP();
    if (cachedIP) {
      return cachedIP;
    }

    for (const provider of this.publicIPProviders) {
      const ip = await this.fetchIPFromProvider(provider.url, provider.key);
      if (ip) {
        this.saveCachedPublicIP(ip);
        return ip;
      }
    }

    const jsonpIP = await this.getIPWithJSONP();
    if (this.isValidIP(jsonpIP)) {
      this.saveCachedPublicIP(jsonpIP);
      return jsonpIP;
    }

    return this.unavailableValue;
  }

  private async fetchIPFromProvider(url: string, key: string): Promise<string | null> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 3500);

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        cache: 'no-store'
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const ip = this.extractValidIP(data?.[key]);
      return this.isValidIP(ip) ? ip : null;
    } catch {
      return null;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  private getIPWithJSONP(): Promise<string> {
    return new Promise((resolve) => {
      if (typeof document === 'undefined') {
        resolve(this.unavailableValue);
        return;
      }

      const windowWithCallbacks = window as unknown as Window & Record<string, unknown>;
      const callbackName = `ipCallback${Date.now()}`;
      const script = document.createElement('script');

      const cleanup = () => {
        delete windowWithCallbacks[callbackName];
        script.remove();
      };

      const timer = window.setTimeout(() => {
        cleanup();
        resolve(this.unavailableValue);
      }, 4000);

      windowWithCallbacks[callbackName] = (data: { ip?: string }) => {
        window.clearTimeout(timer);
        const ip = this.extractValidIP(data?.ip);
        cleanup();
        resolve(ip);
      };

      script.src = `https://api.ipify.org?format=jsonp&callback=${callbackName}`;
      script.async = true;
      script.onerror = () => {
        window.clearTimeout(timer);
        cleanup();
        resolve(this.unavailableValue);
      };

      (document.body || document.head || document.documentElement).appendChild(script);
    });
  }

  private extractValidIP(value: unknown): string {
    if (typeof value !== 'string') {
      return this.unavailableValue;
    }

    const trimmedValue = value.trim();
    return this.isValidIP(trimmedValue) ? trimmedValue : this.unavailableValue;
  }

  private isValidIP(value: string | null | undefined): value is string {
    if (!value || value === this.unavailableValue) {
      return false;
    }

    const ipv4 =
      /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

    if (ipv4.test(value)) {
      return true;
    }

    return value.includes(':') && /^[0-9a-fA-F:]+$/.test(value);
  }

  private detectBrowser(userAgent: string): string {
    if (userAgent.includes('Edg/')) return 'Edge';
    if (userAgent.includes('OPR/') || userAgent.includes('Opera')) return 'Opera';
    if (userAgent.includes('Firefox/')) return 'Firefox';
    if (userAgent.includes('Chrome/')) return 'Chrome';
    if (userAgent.includes('Safari/')) return 'Safari';
    return 'Navegador desconocido';
  }

  private detectBrowserVersion(userAgent: string): string {
    const edgeMatch = userAgent.match(/Edg\/(\d+(\.\d+)*)/);
    if (edgeMatch) {
      return edgeMatch[1];
    }

    const operaMatch = userAgent.match(/(?:OPR|Opera)\/(\d+(\.\d+)*)/);
    if (operaMatch) {
      return operaMatch[1];
    }

    const firefoxMatch = userAgent.match(/Firefox\/(\d+(\.\d+)*)/);
    if (firefoxMatch) {
      return firefoxMatch[1];
    }

    const chromeMatch = userAgent.match(/Chrome\/(\d+(\.\d+)*)/);
    if (chromeMatch) {
      return chromeMatch[1];
    }

    const safariMatch = userAgent.match(/Version\/(\d+(\.\d+)*)/);
    if (safariMatch) {
      return safariMatch[1];
    }

    return 'sin version';
  }

  private detectOS(userAgent: string): string {
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iPhone') || userAgent.includes('iPad') || userAgent.includes('iPod')) return 'iOS';
    if (userAgent.includes('Windows NT')) return 'Windows';
    if (userAgent.includes('Mac OS X') || userAgent.includes('Macintosh')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    return 'SO desconocido';
  }

  private detectDeviceType(userAgent: string): string {
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
      return 'tablet';
    }

    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
      return 'mobile';
    }

    return 'desktop';
  }

  private getCachedPublicIP(): string | null {
    if (this.cachedPublicIP && this.isValidIP(this.cachedPublicIP)) {
      return this.cachedPublicIP;
    }

    try {
      const storedValue = sessionStorage.getItem(this.sessionStorageKey);
      if (storedValue && this.isValidIP(storedValue)) {
        this.cachedPublicIP = storedValue;
        return storedValue;
      }
    } catch {
      return null;
    }

    return null;
  }

  private saveCachedPublicIP(ip: string): void {
    if (!this.isValidIP(ip)) {
      return;
    }

    this.cachedPublicIP = ip;

    try {
      sessionStorage.setItem(this.sessionStorageKey, ip);
    } catch {
      // Ignorar errores si el navegador bloquea el almacenamiento.
    }
  }
}
